/**
 * WT1 - the capture screen, `/tour/:tourId` (plan section E; spec sections 5-6).
 *
 * "Offline capture, online binding" (his decision 2026-09-20): the tour was started with signal, and from here on
 * every photo is DURABLE WITH NONE. A photo is written to this phone's IndexedDB before its panel closes; the drain
 * uploads it when signal allows (on opening, on the `online` event, after each photo, every 20 s while open).
 *
 * ⭐⭐ THE COUNT IS ALWAYS ON SCREEN, never behind a control: the header is sticky and carries it. His ruling -
 *   "defer prose, never a number, and a control must name its contents".
 * ⛔ Rendered WITHOUT Layout (plan section A): Layout's install card sits exactly where the camera button goes, and
 *   its 60 s polls add requests on one bar.
 * ⛔ The new-photo panel is PART OF THE PAGE, in the camera button's place - never a full-screen layer over it (the
 *   OO5d rule; a layer traps a phone's keyboard and scroll). Its note field takes focus so the iOS keyboard opens
 *   with its dictation mic - the app never handles audio.
 * ⭐ The queue is asked for per action (`getTourQueue()`), never kept: iOS drops a suspended app's IndexedDB
 *   connection, and the next ask opens a fresh one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, NetworkError } from '../lib/api';
import {
  drainTour,
  getTourQueue,
  requestPersistentStorage,
  type CaptureRecord,
  type TourCounts,
  type TourRecord,
} from '../lib/tourQueue';
import { tourTransport } from '../lib/tourTransport';
import { TAG_SETS, TAG_SET_LABELS, chipsFor, tagLabel, tagSetFor, type TagCode, type TagSet } from '../lib/tourTags';
import { downscaleToJpeg, makeThumbnail } from '../lib/imageDownscale';
import { describeStartDistance } from '../lib/geo';
import { captureStatus, counterText, countsOf, finishRefusal, unsyncedEdits, type CaptureTone } from '../lib/tourScreen';
import { readInstallEnvironment } from '../utils/installEnvironment';
import { resolveInstallOffer } from '../utils/installPrompt';
import { Button } from '../components/ui';

const DRAIN_EVERY_MS = 20_000;
const NO_COUNTS: TourCounts = { total: 0, uploaded: 0, outstanding: 0, failed: 0 };

const TONE: Record<CaptureTone, string> = {
  done: 'text-emerald-700',
  busy: 'text-gray-600',
  waiting: 'text-amber-700',
  failed: 'text-red-700',
};

const messageOf = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/** A blob's bytes: `arrayBuffer()` where the browser has it (Safari from 14.1), FileReader where it does not. */
const blobBytes = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error ?? new Error('The photo could not be read - take it again.'));
        reader.readAsArrayBuffer(blob);
      });

interface Pending {
  bytes: ArrayBuffer;
  contentType: string;
  thumbnail: string | null;
}

type Phase = { kind: 'loading' } | { kind: 'missing' } | { kind: 'broken'; message: string } | { kind: 'ready' };

/** The tag chips, one tap each. `aria-pressed` says which one is chosen. */
function TagChips({ chips, value, onChange }: { chips: TagCode[]; value: TagCode | null; onChange: (t: TagCode) => void }) {
  return (
    <div role="group" aria-label="Tag" className="flex flex-wrap gap-1.5">
      {chips.map((code) => (
        <button
          key={code}
          type="button"
          aria-pressed={value === code}
          onClick={() => onChange(code)}
          className={`rounded-full border px-3 py-1.5 text-[12.5px] font-semibold ${
            value === code ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-300 bg-white text-gray-800'
          }`}
        >
          {tagLabel(code)}
        </button>
      ))}
    </div>
  );
}

/** The photo just taken: its note, its tag, Save or Discard. In place - part of the page. */
function NewPhotoPanel({
  pending,
  chips,
  onSave,
  onDiscard,
}: {
  pending: Pending;
  chips: TagCode[];
  onSave: (subject: TagCode, note: string) => Promise<void>;
  onDiscard: () => void;
}) {
  const [subject, setSubject] = useState<TagCode | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A switch of tag set can take the chosen tag off the chips; a choice he cannot see is not a choice.
  useEffect(() => {
    if (subject && !chips.includes(subject)) setSubject(null);
  }, [chips, subject]);

  const save = async () => {
    if (!subject) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(subject, note);
    } catch (err) {
      setError(`The photo could not be kept on this phone: ${messageOf(err, 'the phone storage refused it.')}`);
      setSaving(false);
    }
  };

  return (
    <section aria-label="New photo" className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
      {pending.thumbnail ? (
        <img src={pending.thumbnail} alt="The photo just taken" className="max-h-72 w-full rounded-lg object-contain bg-gray-100" />
      ) : (
        <div className="flex h-24 items-center justify-center rounded-lg bg-gray-100 text-[12.5px] text-gray-600">Photo ready</div>
      )}
      <div>
        <label htmlFor="tour-new-note" className="block text-[12px] font-semibold text-gray-700">
          Note about this photo
        </label>
        <textarea
          id="tour-new-note"
          autoFocus
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What does it show? The keyboard's mic takes dictation."
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-[14px]"
        />
      </div>
      <TagChips chips={chips} value={subject} onChange={setSubject} />
      {error && <p role="alert" className="text-[12.5px] text-red-700">{error}</p>}
      <div className="grid grid-cols-2 gap-2">
        <Button size="lg" onClick={() => void save()} disabled={!subject || saving} isLoading={saving}>
          Save photo
        </Button>
        <Button size="lg" variant="outline" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
      </div>
    </section>
  );
}

/** Correct a photo's note or tag - or, while the server cannot have it yet, take it off the phone. */
function EditPhotoPanel({
  capture,
  chips,
  onDone,
}: {
  capture: CaptureRecord;
  chips: TagCode[];
  onDone: (changed: boolean) => void;
}) {
  const [subject, setSubject] = useState<TagCode>(capture.subject);
  const [note, setNote] = useState(capture.note);
  const [error, setError] = useState<string | null>(null);
  const removable = (capture.state === 'pending' || capture.state === 'failed') && !capture.saveAttempted;
  const panel = useRef<HTMLElement>(null);
  // On a long tour the panel opens far below the tile he tapped; bring it to him, or the tap looks like nothing.
  useEffect(() => {
    panel.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, []);

  const save = async () => {
    try {
      const queue = await getTourQueue();
      await queue.updateCapture(capture.id, { subject, note });
      onDone(true);
    } catch (err) {
      setError(messageOf(err, 'The change could not be kept on this phone.'));
    }
  };

  const remove = async () => {
    try {
      const queue = await getTourQueue();
      await queue.discardCapture(capture.id);
      onDone(true);
    } catch (err) {
      setError(messageOf(err, 'The photo could not be removed.'));
    }
  };

  return (
    <section ref={panel} aria-label="Edit photo" className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
      <div>
        <label htmlFor="tour-edit-note" className="block text-[12px] font-semibold text-gray-700">
          Note about this photo
        </label>
        <textarea
          id="tour-edit-note"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-[14px]"
        />
      </div>
      <TagChips chips={chips.includes(subject) ? chips : [subject, ...chips]} value={subject} onChange={setSubject} />
      {error && <p role="alert" className="text-[12.5px] text-red-700">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void save()}>Save changes</Button>
        {removable && (
          <Button variant="outline" onClick={() => void remove()}>
            Remove from this phone
          </Button>
        )}
        <Button variant="ghost" onClick={() => onDone(false)}>
          Close
        </Button>
      </div>
    </section>
  );
}

export default function TourCapture() {
  const { tourId = '' } = useParams();
  const navigate = useNavigate();
  const mounted = useRef(true);
  const notesLoaded = useRef(false);

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [tour, setTour] = useState<TourRecord | null>(null);
  const [captures, setCaptures] = useState<CaptureRecord[]>([]);
  const [counts, setCounts] = useState<TourCounts>(NO_COUNTS);
  const [offline, setOffline] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [finishMessage, setFinishMessage] = useState<string | null>(null);
  const [tagSetError, setTagSetError] = useState<string | null>(null);

  /** Everything the screen shows comes from the PHONE - it works with no signal at all. */
  const refresh = useCallback(async () => {
    try {
      const queue = await getTourQueue();
      const record = await queue.getTour(tourId);
      if (!mounted.current) return;
      if (!record) {
        setPhase({ kind: 'missing' });
        return;
      }
      // ⭐ ONE read: every record carries its photo's bytes until upload, so a second read (`counts`) is another
      //   full copy of every waiting photo, every refresh. The count comes from this list (`countsOf`).
      const list = await queue.listCaptures(tourId);
      if (!mounted.current) return;
      setTour(record);
      setCaptures(list);
      setCounts(countsOf(list));
      if (!notesLoaded.current) {
        notesLoaded.current = true;
        setNotes(record.overallNotes);
      }
      setPhase({ kind: 'ready' });
    } catch (err) {
      if (mounted.current) setPhase({ kind: 'broken', message: messageOf(err, 'The phone storage could not be read.') });
    }
  }, [tourId]);

  /** One pass of the queue's drain. A pass with no signal leaves everything where it was; each tile says why. */
  const drain = useCallback(async () => {
    try {
      const queue = await getTourQueue();
      if (!(await queue.getTour(tourId))) return;
      const summary = await drainTour(queue, tourTransport, tourId);
      if (mounted.current) setOffline(summary.offline);
    } catch {
      // The drain records every refusal on the photo itself; nothing to add here.
    } finally {
      await refresh();
    }
  }, [tourId, refresh]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    void drain();
    void requestPersistentStorage();
    const onOnline = () => void drain();
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(() => void drain(), DRAIN_EVERY_MS);
    return () => {
      mounted.current = false;
      window.removeEventListener('online', onOnline);
      window.clearInterval(timer);
    };
  }, [refresh, drain]);

  const tagSet: TagSet = tour?.tagSet ?? tagSetFor(tour?.propertyType);
  const chips = useMemo(() => chipsFor(tagSet), [tagSet]);
  const distanceLine = tour
    ? describeStartDistance({ lat: tour.startLat, lng: tour.startLng }, { lat: tour.propertyLat, lng: tour.propertyLng })
    : null;
  const safariTab = useMemo(() => resolveInstallOffer(readInstallEnvironment(false)) === 'ios-instructions', []);
  const editingCapture = editing ? captures.find((c) => c.id === editing) ?? null : null;

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = ''; // the same shot can be picked again
    if (!file) return;
    setPickError(null);
    setPreparing(true);
    try {
      const blob = await downscaleToJpeg(file);
      const [bytes, thumbnail] = await Promise.all([blobBytes(blob), makeThumbnail(blob)]);
      if (!bytes.byteLength) throw new Error('The camera handed back an empty photo - take it again.');
      if (mounted.current) setPending({ bytes, contentType: blob.type || file.type || 'image/jpeg', thumbnail });
    } catch (err) {
      if (mounted.current) setPickError(messageOf(err, 'The photo could not be read - take it again.'));
    } finally {
      if (mounted.current) setPreparing(false);
    }
  };

  /** ⛔ The panel closes only AFTER the phone has the photo - `addCapture` resolves on the transaction's commit. */
  const saveNewPhoto = async (subject: TagCode, note: string) => {
    if (!pending) return;
    const queue = await getTourQueue();
    await queue.addCapture({
      tourId,
      bytes: pending.bytes,
      contentType: pending.contentType,
      thumbnail: pending.thumbnail,
      subject,
      note,
    });
    if (!mounted.current) return;
    setPending(null);
    await refresh();
    void drain();
  };

  const saveNotes = async () => {
    if (!tour || notes === tour.overallNotes) return;
    try {
      const queue = await getTourQueue();
      await queue.setNotes(tourId, notes);
      await refresh();
      void drain();
    } catch (err) {
      setFinishMessage(`The notes could not be kept on this phone: ${messageOf(err, 'the phone storage refused them.')}`);
    }
  };

  const switchTagSet = async (set: TagSet) => {
    setTagSetError(null);
    try {
      const queue = await getTourQueue();
      await queue.setTagSet(tourId, set);
    } catch (err) {
      setTagSetError(`The tag set could not be kept on this phone: ${messageOf(err, 'the phone storage refused it.')}`);
      return;
    }
    await refresh();
    void drain();
  };

  /**
   * ⛔ NOTHING MAY BE STRANDED ON THE PHONE BY FINISHING. After Finish only this screen drains, and a finished tour is
   *   not reopened here - so a photo still in its panel, a photo not yet uploaded, or notes and edits not yet sent
   *   would never leave the phone, and the report would say there were none. Send what can be sent FIRST, then look.
   */
  const finish = async () => {
    setFinishMessage(null);
    if (pending) {
      setFinishMessage(finishRefusal(NO_COUNTS, { pendingPhoto: true }));
      return;
    }
    setFinishing(true);
    try {
      await drain();
      const queue = await getTourQueue();
      const [record, list] = await Promise.all([queue.getTour(tourId), queue.listCaptures(tourId)]);
      const c = countsOf(list);
      const refusal = finishRefusal(c, { unsynced: record ? unsyncedEdits(record, list) : 0 });
      if (refusal) {
        if (mounted.current) setFinishMessage(refusal);
        return;
      }
      await api.patchTour(tourId, { status: 'complete', captured_count: c.total });
      navigate(`/tour/${tourId}/report`);
    } catch (err) {
      if (!mounted.current) return;
      setFinishMessage(
        err instanceof NetworkError
          ? 'Finishing needs signal. Every photo is safe on this phone - try again when you have a bar.'
          : messageOf(err, 'The walk-through could not be finished - try again.'),
      );
    } finally {
      if (mounted.current) setFinishing(false);
    }
  };

  if (phase.kind === 'loading') {
    return <p className="p-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] text-[13px] text-gray-600">Opening the walk-through</p>;
  }

  if (phase.kind === 'missing' || phase.kind === 'broken') {
    return (
      <main className="mx-auto max-w-xl space-y-3 p-4 pt-[calc(env(safe-area-inset-top)+1rem)]">
        <p className="text-[15px] font-semibold text-gray-900">
          {phase.kind === 'missing' ? 'This walk-through is not on this phone.' : 'The walk-through could not be opened.'}
        </p>
        <p className="text-[13px] text-gray-600">
          {phase.kind === 'missing'
            ? "It was started on another phone, or this phone's storage was cleared. Its report shows everything that has uploaded."
            : phase.message}
        </p>
        <div className="flex flex-wrap gap-4 text-[13px] font-semibold">
          <Link to={`/tour/${tourId}/report`} className="text-primary-500">Open its report</Link>
          <Link to="/tours" className="text-primary-500">All walk-throughs</Link>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-[calc(env(safe-area-inset-bottom)+2.5rem)]">
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 pb-2.5 pt-[calc(env(safe-area-inset-top)+10px)]">
        <div className="mx-auto max-w-xl">
          <Link
            to="/tours"
            className="text-[12px] font-semibold text-primary-500"
            onClick={(e) => {
              // The photo in the panel lives only in this screen until it is saved.
              if (pending && !window.confirm('Leave without saving the photo you just took? It is not on the phone yet.')) {
                e.preventDefault();
              }
            }}
          >
            Walk-throughs
          </Link>
          <h1 className="truncate text-[15px] font-semibold text-gray-900">{tour?.propertyLabel}</h1>
          <p data-testid="tour-counter" role="status" aria-live="polite" className="text-[14px] font-semibold text-gray-900">
            {counterText(counts)}
          </p>
          {offline && counts.outstanding > 0 && (
            <p className="text-[12px] text-amber-700">No signal - they upload when it returns.</p>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-xl space-y-4 p-4">
        {safariTab && (
          <p role="note" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
            You are in a Safari tab. Safari can clear photos that have not uploaded if the app sits unused for a week.
            Add Apex to your Home Screen (Share, then Add to Home Screen) before a long walk-through.
          </p>
        )}
        {distanceLine && <p className="text-[12.5px] font-semibold text-amber-800">{distanceLine}</p>}
        {tour?.syncError && (
          <p role="alert" className="text-[12.5px] text-red-700">
            The notes or tag set did not save: {tour.syncError}
          </p>
        )}

        {pending ? (
          <NewPhotoPanel pending={pending} chips={chips} onSave={saveNewPhoto} onDiscard={() => setPending(null)} />
        ) : (
          <div>
            <input
              id="tour-camera"
              type="file"
              accept="image/*"
              capture="environment"
              className="sr-only"
              onChange={(e) => void onPick(e)}
              disabled={preparing}
            />
            <label
              htmlFor="tour-camera"
              className="flex h-16 w-full cursor-pointer items-center justify-center rounded-xl bg-primary-500 text-[16px] font-semibold text-white active:bg-primary-700"
            >
              {preparing ? 'Getting the photo ready' : 'Take a photo'}
            </label>
            {pickError && <p role="alert" className="mt-2 text-[12.5px] text-red-700">{pickError}</p>}
          </div>
        )}

        <div className="flex items-center gap-2 text-[12.5px] text-gray-700">
          <label htmlFor="tour-tag-set" className="font-semibold">
            Tag set
          </label>
          <select
            id="tour-tag-set"
            value={tagSet}
            onChange={(e) => void switchTagSet(e.target.value as TagSet)}
            className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-[13px]"
          >
            {TAG_SETS.map((set) => (
              <option key={set} value={set}>
                {TAG_SET_LABELS[set]}
              </option>
            ))}
          </select>
        </div>
        {tagSetError && (
          <p role="alert" className="text-[12.5px] text-red-700">
            {tagSetError}
          </p>
        )}

        {captures.length > 0 && (
          <ul aria-label="Photos on this walk-through" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {captures.map((c) => {
              const status = captureStatus(c, { offline });
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setEditing(c.id)}
                    className="block w-full rounded-lg border border-gray-200 bg-white p-1.5 text-left"
                  >
                    {c.thumbnail ? (
                      <img src={c.thumbnail} alt="" className="aspect-[4/3] w-full rounded object-cover" />
                    ) : (
                      <span className="block aspect-[4/3] w-full rounded bg-gray-100" />
                    )}
                    <span className="mt-1 block text-[12px] font-semibold text-gray-900">{tagLabel(c.subject)}</span>
                    {c.note && <span className="block break-words text-[11.5px] text-gray-600">{c.note}</span>}
                    <span className={`block break-words text-[11.5px] font-semibold ${TONE[status.tone]}`}>{status.text}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {editingCapture && (
          <EditPhotoPanel
            key={editingCapture.id}
            capture={editingCapture}
            chips={chips}
            onDone={(changed) => {
              setEditing(null);
              if (changed) {
                void refresh();
                void drain();
              }
            }}
          />
        )}

        <div>
          <label htmlFor="tour-notes" className="block text-[12px] font-semibold text-gray-700">
            Overall notes
          </label>
          <textarea
            id="tour-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => void saveNotes()}
            placeholder="Anything about the building as a whole."
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-[14px]"
          />
        </div>

        <div className="space-y-2">
          <Button size="lg" className="w-full" onClick={() => void finish()} isLoading={finishing}>
            Finish tour
          </Button>
          {finishMessage && (
            <p role="alert" className="text-[13px] font-semibold text-red-700">
              {finishMessage}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
